import {DocumentSubStatus, Nature, NatureCycle} from "src/_generated/typed-document-nodes"
import {DocumentType} from "src/utils/@types/DocumentType"
import CommonUtils from "src/utils/Common"

// based on NatureCycle enum generated by graphql
const NATURE_CYCLE = Object.fromEntries(Object.values(NatureCycle).map((v) => [v, v]))

const NATURE_CODE_STARTS_BY = Object.fromEntries(
    Object.values(NatureCycle).map((v) => {
        const valEscaped = v.replaceAll("_", "")
        return [valEscaped, valEscaped]
    })
)

// create union type from the values of the object
export type NatureCycleUnion = (typeof NATURE_CYCLE)[keyof typeof NATURE_CYCLE]

const DOCUMENTS_TO_NOT_REQUEST = ["O2CMisc", "P2PMisc"]
const CHORUS_NATURES = ["ChorusB2G"]

/** The sub statuses a document ends on, which nothing more is sent for. */
const SETTLED_DOCUMENT_SUB_STATUSES: DocumentSubStatus[] = [DocumentSubStatus.Canceled, DocumentSubStatus.Error]

/**
 * Whether a document is still live, rather than given up on. A settled one takes no further sending.
 *
 * @param {DocumentSubStatus | string | null | undefined} subStatus - Sub status the document stands at.
 * @returns {boolean} False once the document is canceled or in error, true otherwise.
 */
const isLiveDocument = (subStatus: DocumentSubStatus | string | null | undefined): boolean =>
    !SETTLED_DOCUMENT_SUB_STATUSES.includes(subStatus as DocumentSubStatus)

/**
 * Document types whose flows the service cannot send again yet.
 */
const DOCUMENT_TYPES_WITHOUT_RESEND = ["O2CEinv", "P2PEinv", "MiscInvalid"]

/**
 * Whether the flow of a document can be sent again.
 *
 * @param {string | null | undefined} documentTypeCode - Code of the type the document was filed under.
 * @returns {boolean} False for the types the service cannot send again, true otherwise.
 */
const isResendableDocumentType = (documentTypeCode: string | null | undefined): boolean =>
    !DOCUMENT_TYPES_WITHOUT_RESEND.some((code) => code.toUpperCase() === (documentTypeCode ?? "").toUpperCase())

/**
 * Document types the service cannot add a recipient to yet. Kept apart from
 * DOCUMENT_TYPES_WITHOUT_RESEND, so lifting one hold leaves the other standing.
 */
const DOCUMENT_TYPES_WITHOUT_NEW_RECIPIENT = ["O2CEinv", "P2PEinv", "MiscInvalid"]

/**
 * Whether a recipient can be added to a document.
 *
 * @param {string | null | undefined} documentTypeCode - Code of the type the document was filed under.
 * @returns {boolean} False for the types the service takes no new recipient for, true otherwise.
 */
const canAddNewRecipient = (documentTypeCode: string | null | undefined): boolean =>
    !DOCUMENT_TYPES_WITHOUT_NEW_RECIPIENT.some((code) => code.toUpperCase() === (documentTypeCode ?? "").toUpperCase())

const isO2C = (cycle: string) => cycle === NATURE_CYCLE.O_2_C
const isP2P = (cycle: string) => cycle === NATURE_CYCLE.P_2_P
const isOldReform = (cycle: string) => cycle === "O2CInv" || cycle === "P2PInv"

const getNatureCycleByDocumentType = (documentType: string) => {
    const existingDocumentType: DocumentType = CommonUtils.GetAllDocTypes().find(
        (item: DocumentType) => item.code === documentType
    )

    const nature: Nature = CommonUtils.GetAllNatures().find(
        (item: Nature) => item.code?.toUpperCase() === existingDocumentType.natureCode?.toUpperCase()
    )

    return nature.cycle
}

const MISC = "MISC"
const O2C = "O2C"
const P2P = "P2P"
const EINVOICE = "EINV"

/**
 * Sub type code the service files the attachments of a CDAR, the structured life cycle
 * file, under. They land in the invoice `attachmentIds` next to its own attachments.
 */
const CDAR_ATTACHMENT_SUB_TYPE_CODE = "PJCDV"

/**
 * Whether a document is an attachment carried by a CDAR rather than one filed with the invoice.
 *
 * @param {string | null | undefined} documentSubTypeCode - Sub type code the document was filed under.
 * @returns {boolean} True for a CDAR attachment, false otherwise.
 */
const isCdarAttachmentSubType = (documentSubTypeCode: string | null | undefined): boolean =>
    (documentSubTypeCode ?? "").toUpperCase() === CDAR_ATTACHMENT_SUB_TYPE_CODE

export {
    canAddNewRecipient,
    CDAR_ATTACHMENT_SUB_TYPE_CODE,
    CHORUS_NATURES,
    DOCUMENT_TYPES_WITHOUT_NEW_RECIPIENT,
    DOCUMENT_TYPES_WITHOUT_RESEND,
    DOCUMENTS_TO_NOT_REQUEST,
    EINVOICE,
    getNatureCycleByDocumentType,
    isCdarAttachmentSubType,
    isLiveDocument,
    isO2C,
    isOldReform,
    isP2P,
    isResendableDocumentType,
    MISC,
    NATURE_CODE_STARTS_BY,
    NATURE_CYCLE,
    O2C,
    P2P
}
